'use client';

import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { Quote } from '@/util/types/serverTypes';

type AutoSaveDraftParams = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  contactId?: string;
  selectedRoleId?: string;
  people?: any[];
  address?: any;
  property?: any;
  requiredInfoValues?: any[];
  equipmentQuantities?: any[];
  /** `YYYY-MM-DD`, or `null` to clear one the customer took back off. */
  closingDate?: string | null;
  _inspectorRequestedIds?: string[];
  duration?: number;
  total?: number;
  // Service selections
  selectedPrimaryServiceId?: string;
  selectedBundleId?: string;
  selectedAddonIds?: string[];
  // Charge objects
  charges?: any[];
  // Draft identification
  currentDraftId?: string;
  // Escape hatch reason enum
  schedulingBlockedOnline?: 'service-blocked' | 'out-of-area' | 'size-limit' | null;
  // WhatConverts lead ID
  wcLeadId?: string | null;
  // Auth tokens (required)
  jwtToken: string;
  companyId: string;
};

/**
 * Saves or updates a draft quote with charges
 * @param params - Draft quote data and auth tokens
 * @returns Quote object directly, or null on error
 */
export async function saveDraftQuote(
  params: AutoSaveDraftParams
): Promise<Quote | null> {
  try {
    const isUpdate = Boolean(params.currentDraftId);
    const quoteData = {
      _companyId: params.companyId,
      property: params.property,
      requiredInfoValues: params.requiredInfoValues,
      equipmentQuantities: params.equipmentQuantities,
      closingDate: params.closingDate,
      _inspectorRequestedIds: params._inspectorRequestedIds,
      duration: params.duration,
      total: params.total,
      selectedPrimaryServiceId: params.selectedPrimaryServiceId,
      selectedBundleId: params.selectedBundleId,
      selectedAddonIds: params.selectedAddonIds,
      people: params.people,
      schedulingBlockedOnline: params.schedulingBlockedOnline,
      wcLeadId: params.wcLeadId,
    };

    if (isUpdate) {
      // Update existing draft: Use PATCH and omit status to preserve backend status (e.g., 'abandoned')
      const response = await callServerAction<Quote>({
        url: `quote/with-charges/${params.currentDraftId}`,
        method: 'patch',
        data: {
          quote: quoteData,
          charges: params.charges,
        },
        params: { _companyId: params.companyId },
        headers: { 'client-token': params.jwtToken },
      });

      // Backend returns quote directly
      return response;
    } else {
      // Create new draft: Use POST and include status: 'draft'
      // Backend returns Quote directly
      const response = await callServerAction<Quote>({
        url: 'quote/with-charges',
        method: 'post',
        data: {
          status: 'draft',
          quote: quoteData,
          charges: params.charges,
          contactData: {
            email: params.email,
            firstName: params.firstName,
            lastName: params.lastName,
            phone: params.phone,
            contactId: params.contactId,
            selectedRoleId: params.selectedRoleId,
          },
        },
        params: { _companyId: params.companyId },
        headers: { 'client-token': params.jwtToken },
      });

      // Backend returns quote directly
      return response;
    }
  } catch (error) {
    console.error('[Lead Capture] Failed to save draft:', error);
    return null;
  }
}
